/**
 * Development Logging Utility
 *
 * Provides categorized logging that only appears in development mode.
 * Helps keep production builds clean while maintaining useful debug information during development.
 *
 * Usage:
 * ```typescript
 * import { devLog, devWarn, devError } from '@/utils/devLog';
 *
 * devLog('CHART', 'Rendering treemap', { componentId, height });
 * devWarn('LAYOUT', 'Unexpected flex direction', { direction });
 * devError('API', 'Failed to fetch data', error);
 * ```
 */

type LogCategory =
  | 'CHART'
  | 'LAYOUT'
  | 'SELECTION'
  | 'AI'
  | 'PERFORMANCE'
  | 'RENDER'
  | 'STORE'
  | 'VIEWPORT'
  | 'KEYBOARD'
  | 'INTERACTION'
  | 'GENERAL';

interface DevLogConfig {
  enabled: boolean;
  categories: Set<LogCategory | 'ALL'>;
  useColors: boolean;
}

// Configuration - can be modified at runtime
// Disabled by default to reduce console noise. Set VITE_ENABLE_DEV_LOGS=true to enable.
const config: DevLogConfig = {
  enabled: import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_LOGS === 'true',
  categories: new Set(['ALL']), // Log all categories by default
  useColors: true,
};

// Category colors for better visual distinction
const categoryColors: Record<LogCategory, string> = {
  CHART: '#10b981', // green
  LAYOUT: '#3b82f6', // blue
  SELECTION: '#a855f7', // purple
  AI: '#f59e0b', // amber
  PERFORMANCE: '#ef4444', // red
  RENDER: '#06b6d4', // cyan
  STORE: '#8b5cf6', // violet
  VIEWPORT: '#14b8a6', // teal
  KEYBOARD: '#ec4899', // pink
  INTERACTION: '#6366f1', // indigo
  GENERAL: '#6b7280', // gray
};

/**
 * Enable or disable dev logging globally
 */
export function setDevLogEnabled(enabled: boolean): void {
  config.enabled = enabled;
}

/**
 * Enable logging for specific categories only
 * @example enableCategories(['CHART', 'LAYOUT'])
 */
export function enableCategories(categories: LogCategory[]): void {
  config.categories = new Set(categories);
}

/**
 * Enable logging for all categories
 */
export function enableAllCategories(): void {
  config.categories = new Set(['ALL']);
}

/**
 * Check if a category should be logged
 */
function shouldLog(category: LogCategory): boolean {
  if (!config.enabled) return false;
  if (config.categories.has('ALL')) return true;
  return config.categories.has(category);
}

/**
 * Format the log message with category prefix
 */
function formatMessage(category: LogCategory, message: string): string {
  return `[${category}] ${message}`;
}

/**
 * Apply color styling for console output (browser only)
 */
function getStyles(category: LogCategory): string[] {
  if (!config.useColors || typeof window === 'undefined') {
    return [];
  }
  return [
    `color: ${categoryColors[category]}; font-weight: bold;`,
    'color: inherit; font-weight: normal;',
  ];
}

/**
 * Development log - only appears in development mode
 *
 * @param category - Category for filtering and color coding
 * @param message - Log message
 * @param data - Optional data to log
 */
export function devLog(category: LogCategory, message: string, data?: any): void {
  if (!shouldLog(category)) return;

  const formattedMessage = formatMessage(category, message);
  const styles = getStyles(category);

  if (styles.length > 0 && data !== undefined) {
    console.log(`%c${formattedMessage}`, styles[0], data);
  } else if (styles.length > 0) {
    console.log(`%c${formattedMessage}`, styles[0]);
  } else if (data !== undefined) {
    console.log(formattedMessage, data);
  } else {
    console.log(formattedMessage);
  }
}

/**
 * Development warning - only appears in development mode
 */
export function devWarn(category: LogCategory, message: string, data?: any): void {
  if (!shouldLog(category)) return;

  const formattedMessage = formatMessage(category, message);

  if (data !== undefined) {
    console.warn(formattedMessage, data);
  } else {
    console.warn(formattedMessage);
  }
}

/**
 * Development error - only appears in development mode
 */
export function devError(category: LogCategory, message: string, error?: any): void {
  if (!shouldLog(category)) return;

  const formattedMessage = formatMessage(category, message);

  if (error !== undefined) {
    console.error(formattedMessage, error);
  } else {
    console.error(formattedMessage);
  }
}

/**
 * Development table - only appears in development mode
 * Useful for logging arrays of objects
 */
export function devTable(category: LogCategory, message: string, data: any[]): void {
  if (!shouldLog(category)) return;

  const formattedMessage = formatMessage(category, message);
  console.log(formattedMessage);
  console.table(data);
}

/**
 * Development group - only appears in development mode
 * Creates a collapsible group in the console
 */
export function devGroup(category: LogCategory, groupName: string, callback: () => void): void {
  if (!shouldLog(category)) return;

  const formattedMessage = formatMessage(category, groupName);
  console.group(formattedMessage);
  callback();
  console.groupEnd();
}

/**
 * Development time measurement - only appears in development mode
 * Measures execution time of a function
 */
export async function devTime<T>(
  category: LogCategory,
  label: string,
  callback: () => T | Promise<T>
): Promise<T> {
  if (!shouldLog(category)) {
    return callback();
  }

  const formattedLabel = formatMessage(category, label);
  console.time(formattedLabel);

  try {
    const result = await callback();
    console.timeEnd(formattedLabel);
    return result;
  } catch (error) {
    console.timeEnd(formattedLabel);
    throw error;
  }
}

// Export configuration for runtime modifications
export const devLogConfig = config;
